//
// # Schematic Instance Builder
//
// Factory for creating schematic instances with proper defaults and validation.
// Handles all instance-specific domain logic including:
// - Name generation
// - Position calculation with offset
// - Generator resolution
// - Parameter conversion
//

import { Result, Context } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import type { SchematicChange } from "./change";
import { Builtin } from "../builtins";

// Type for generator registry interface (avoid circular dependencies)
export interface GeneratorRegistry {
  resolve(ctx: Context, symbolName: string, pdkId?: string): any;
}

// Design state interface (minimal subset needed)
export interface SchematicDesignState {
  instances: {
    size(): number;
  };
}

export interface CreateInstanceOptions {
  ctx: Context;
  design: SchematicDesignState;
  symbolName: string;
  pdkId?: string;
  params?: Record<string, unknown>;
  position?: { x: number; y: number };
  name?: string;
  generatorRegistry: GeneratorRegistry;
  // When provided, use this ident directly as the instance target instead of
  // resolving via generator registry. Used for instancing depot designs.
  targetIdent?: schema.Ident;
}

export interface CreateInstanceResult {
  change: SchematicChange;
  instanceName: string;
}

export interface UpdateInstanceOptions {
  instance: schema.schematic.Instance;
  name?: string;
  params?: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface UpdateInstanceResult {
  change: SchematicChange;
}

export interface DeleteInstanceOptions {
  instanceId: number;
}

export interface DeleteInstanceResult {
  change: SchematicChange;
}

export class SchematicInstanceBuilder {
  // Default offset between instances when no position specified
  private static readonly DEFAULT_OFFSET = 100;

  /**
   * Create a new schematic instance
   * Returns a Change object ready to be applied to the design
   */
  static create(options: CreateInstanceOptions): Result<CreateInstanceResult> {
    const {
      ctx,
      design,
      symbolName,
      pdkId,
      params,
      position,
      name,
      generatorRegistry,
      targetIdent: explicitTargetIdent,
    } = options;

    // Generate instance name: use provided name, or derive from targetIdent's
    // last path segment, or fall back to symbolName-based naming.
    const instanceCount = design.instances.size();
    const derivedName =
      explicitTargetIdent?.kind === "hierpath"
        ? explicitTargetIdent.path[explicitTargetIdent.path.length - 1]
        : symbolName;
    const instanceName =
      name || this.generateInstanceName(derivedName, instanceCount);

    // Calculate position with offset if not provided
    const instancePosition =
      position || this.calculateDefaultPosition(instanceCount);

    // Determine target identifier and symbolType
    let targetIdent: schema.Ident;
    let symbolType: schema.BuiltinSymbolType | undefined;
    if (explicitTargetIdent) {
      // Explicit target ident provided (depot design instancing) - skip resolution
      targetIdent = explicitTargetIdent;
      // symbolType not available for explicit targets
    } else {
      // Resolve generator and build target ident
      const generator = this.resolveGenerator(
        ctx,
        symbolName,
        pdkId,
        generatorRegistry
      );
      targetIdent = this.buildTargetIdent(symbolName, pdkId, generator);
      // Extract symbolType from generator or builtin for device classification
      symbolType = this.extractSymbolType(symbolName, generator);
    }

    // Convert params to schematic format
    const schemaParams = params ? this.convertParamsToSchema(params) : [];

    // Build instance data with targetKind for Change type
    // Include symbolType for device classification (used by symmetry inference)
    const instanceData: {
      targetKind: "instance";
      name: string;
      target: schema.Ident;
      xform: schema.core.Xform;
      params: schema.schematic.InstanceParam[];
      symbolType?: schema.BuiltinSymbolType;
    } = {
      targetKind: "instance" as const,
      name: instanceName,
      target: targetIdent,
      xform: {
        x: instancePosition.x,
        y: instancePosition.y,
        reflected: false,
        rotation: 0 as schema.core.Rotation,
      },
      params: schemaParams,
    };

    // Only include symbolType if it was determined
    if (symbolType) {
      instanceData.symbolType = symbolType;
    }

    // Create add change
    const change: SchematicChange = {
      contentKind: "schematic",
      changeKind: "add",
      data: instanceData,
    };

    return Result.Ok({
      change,
      instanceName,
    });
  }

  /**
   * Update an existing schematic instance
   * Returns a Change object ready to be applied to the design
   */
  static update(options: UpdateInstanceOptions): Result<UpdateInstanceResult> {
    const { instance, name, params, position } = options;

    if (instance.id === undefined) {
      return Result.Err({
        why: "missing-instance-id",
        message: "Instance must have an ID to be updated",
      });
    }

    // Build update data
    const updateData = {
      targetKind: "instance" as const,
      id: instance.id,
      name: name || instance.name,
      target: instance.target,
      xform: position
        ? {
            x: position.x,
            y: position.y,
            reflected: instance.xform.reflected,
            rotation: instance.xform.rotation,
          }
        : instance.xform,
      params: params
        ? this.mergeParamsToSchema(instance.params ?? [], params)
        : instance.params,
    };

    // Create update change
    const change: SchematicChange = {
      contentKind: "schematic",
      changeKind: "update",
      id: instance.id,
      data: updateData,
    };

    return Result.Ok({ change });
  }

  /**
   * Delete an existing schematic instance
   * Returns a Change object ready to be applied to the design
   */
  static delete(options: DeleteInstanceOptions): Result<DeleteInstanceResult> {
    const { instanceId } = options;

    // Create remove change
    const change: SchematicChange = {
      contentKind: "schematic",
      changeKind: "remove",
      id: instanceId,
      data: {
        targetKind: "instance" as const,
      },
    };

    return Result.Ok({ change });
  }

  /**
   * Generate instance name from symbol name and count
   * Examples: "nmos" -> "N0", "res" -> "R0"
   */
  private static generateInstanceName(
    symbolName: string,
    count: number
  ): string {
    const firstChar = symbolName[0]?.toUpperCase() || "X";
    return `${firstChar}${count}`;
  }

  /**
   * Calculate default position with diagonal offset to avoid overlaps
   */
  private static calculateDefaultPosition(instanceCount: number): {
    x: number;
    y: number;
  } {
    return {
      x: instanceCount * this.DEFAULT_OFFSET,
      y: instanceCount * this.DEFAULT_OFFSET,
    };
  }

  /**
   * Resolve generator - try PDK-specific first, then generic
   */
  private static resolveGenerator(
    ctx: Context,
    symbolName: string,
    pdkId: string | undefined,
    generatorRegistry: GeneratorRegistry
  ): any {
    if (pdkId) {
      const pdkGenerator = generatorRegistry.resolve(ctx, symbolName, pdkId);
      if (pdkGenerator) return pdkGenerator;
    }
    return generatorRegistry.resolve(ctx, symbolName);
  }

  /**
   * Build target identifier based on whether it's a generator or builtin
   */
  private static buildTargetIdent(
    symbolName: string,
    pdkId: string | undefined,
    generator: unknown
  ): schema.Ident {
    if (generator) {
      // Generator instance - reference generator path
      // Schematics don't generate content, they reference the generator
      const generatorPath = pdkId
        ? ["generators", pdkId, symbolName]
        : ["generators", symbolName];

      return {
        kind: "hierpath",
        path: generatorPath,
      };
    } else {
      // Check if this is a known builtin symbol
      const builtin = Builtin.find(symbolName);
      if (builtin) {
        // Use proper builtin ident for builtin symbols
        return {
          kind: "builtin",
          id: builtin.id,
        };
      }
      // Fall back to hierpath for unknown symbols
      return {
        kind: "hierpath",
        path: ["symbols", symbolName],
      };
    }
  }

  /**
   * Extract symbolType from generator or builtin for device classification.
   * Used by symmetry inference to identify transistor types (nmos, pmos, etc.)
   */
  private static extractSymbolType(
    symbolName: string,
    generator: unknown
  ): schema.BuiltinSymbolType | undefined {
    if (generator) {
      // Generator - get symbolType from describe()
      const gen = generator as { describe?: () => { symbolType?: string } };
      if (gen.describe) {
        const spec = gen.describe();
        // Validate that symbolType is a valid BuiltinSymbolType
        if (spec.symbolType) {
          return spec.symbolType as schema.BuiltinSymbolType;
        }
      }
      return undefined;
    } else {
      // Check if this is a known builtin - builtin id IS the symbolType, with
      // one exception: `no_connect` is a non-functional annotation that
      // contributes nothing to netlist extraction, so it must not carry a
      // device-classification symbolType (which would also fail content
      // validation, since `no_connect` is not a member of BuiltinSymbolType).
      const builtin = Builtin.find(symbolName);
      if (!builtin || builtin.id === "no_connect") {
        return undefined;
      }
      return builtin.id;
    }
  }

  /**
   * Convert params Record to schematic parameter array format
   */
  private static convertParamsToSchema(
    params: Record<string, unknown>
  ): schema.schematic.InstanceParam[] {
    return Object.entries(params).map(([name, value]) => ({
      kind: "string" as const,
      name,
      value: String(value),
    }));
  }

  /**
   * Merge new params into existing params. Existing params are preserved;
   * new params override matching names and add new ones.
   */
  private static mergeParamsToSchema(
    existing: schema.schematic.InstanceParam[],
    newParams: Record<string, unknown>
  ): schema.schematic.InstanceParam[] {
    // Build a map from existing params
    const merged = new Map<string, string>();
    for (const p of existing) {
      if (Array.isArray(p)) {
        merged.set(p[0], p[1]);
      } else {
        merged.set(p.name, p.value);
      }
    }
    // Override/add new params
    for (const [name, value] of Object.entries(newParams)) {
      merged.set(name, String(value));
    }
    // Convert back to schema format
    return Array.from(merged.entries()).map(([name, value]) => ({
      kind: "string" as const,
      name,
      value,
    }));
  }
}
